import { ThemeProvider } from "@mui/material/styles";
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  DarkThemeWithResponsiveFontSizes as DarkTheme,
  LightThemeWithResponsiveFontSizes as LightTheme,
} from "../theme/theme";

import {
  DevDarkThemeWithResponsiveFontSizes,
  DevLightThemeWithResponsiveFontSizes,
} from "../theme/theme.dev";
import { getRuntimeEnv } from "./getRuntimeEnv";
import { createAppTheme, ThemeColors } from "../theme/theme.base";
import { prodColors } from "../theme/theme.tokens";
import { getActiveHoliday, HolidayType } from "../utils/holidays";

const HOLIDAY_PRIMARY: Record<
  HolidayType,
  Record<"light" | "dark", ThemeColors["primary"]>
> = {
  milli_bayram: {
    light: { main: "#C0392B", light: "#E74C3C", dark: "#962D22" },
    dark: { main: "#E53935", light: "#FF6659", dark: "#AB000D" },
  },
  kasim10: {
    light: { main: "#424242", light: "#616161", dark: "#212121" },
    dark: { main: "#9E9E9E", light: "#BDBDBD", dark: "#757575" },
  },
};

type ThemeProfile = "dev" | "prod";

type ColorModeContextType = {
  mode: string;
  toggleMode: () => void;
  isDevEnv: boolean;
  themeProfile: ThemeProfile;
  toggleThemeProfile: () => void;
};

export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType,
);

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const colorModeFromLocalStorage = localStorage.getItem("colorMode");
  const isSystemPreferenceDark = window?.matchMedia(
    "(prefers-color-scheme: dark)",
  ).matches;

  const systemPreference = isSystemPreferenceDark ? "dark" : "light";
  const [mode, setMode] = useState(
    colorModeFromLocalStorage ?? systemPreference,
  );
  const runtimeEnv = useMemo(() => getRuntimeEnv(), []);
  const isDevEnv = runtimeEnv === "dev";
  const activeHoliday = useMemo(() => getActiveHoliday(), []);

  // The prod/dev theme profile can only be overridden while actually running
  // in a dev environment; deployed prod hosts always render the prod theme.
  const [themeProfile, setThemeProfile] = useState<ThemeProfile>(() => {
    if (!isDevEnv) return "prod";
    const stored = globalThis.localStorage.getItem("themeProfile");
    return stored === "prod" ? "prod" : "dev";
  });

  const theme = useMemo(() => {
    const modeKey = mode as "light" | "dark";

    if (isDevEnv && themeProfile === "dev") {
      return modeKey === "light"
        ? DevLightThemeWithResponsiveFontSizes
        : DevDarkThemeWithResponsiveFontSizes;
    }

    if (activeHoliday) {
      const base = prodColors[modeKey];
      const holidayColors: ThemeColors = {
        ...base,
        primary: HOLIDAY_PRIMARY[activeHoliday.type][modeKey],
      };
      return createAppTheme(modeKey, holidayColors);
    }

    return modeKey === "light" ? LightTheme : DarkTheme;
  }, [mode, isDevEnv, themeProfile, activeHoliday]);

  useEffect(() => {
    window.localStorage.setItem("colorMode", mode);
  }, [mode]);

  useEffect(() => {
    if (!isDevEnv) return;
    globalThis.localStorage.setItem("themeProfile", themeProfile);
  }, [isDevEnv, themeProfile]);

  const toggleColorMode = useCallback(() => {
    if (mode === "light") {
      setMode("dark");
    } else {
      setMode("light");
    }
  }, [mode]);

  const toggleThemeProfile = useCallback(() => {
    if (!isDevEnv) return;
    setThemeProfile((prev) => (prev === "dev" ? "prod" : "dev"));
  }, [isDevEnv]);

  const colorModeValue = useMemo(
    () => ({
      toggleMode: toggleColorMode,
      mode,
      isDevEnv,
      themeProfile,
      toggleThemeProfile,
    }),
    [mode, toggleColorMode, isDevEnv, themeProfile, toggleThemeProfile],
  );

  return (
    <ColorModeContext.Provider value={colorModeValue}>
      <ThemeProvider
        // you can change the theme colors here. example: mode === "light" ? RefineThemes.Magenta : RefineThemes.MagentaDark
        theme={theme}
      >
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
